import { useContext, type ComponentType } from 'react';
import { HoruzProtocolChulakServiceSessionGuardTags } from '@ver.id/libs.internal.horuz';
import { ChulakState } from './protocol';
import { useChulakSession } from '../hooks/useChulakSession';
import { type AppInfo } from '../types';
import { JourneyContext } from '../hooks/useJourney';
import {
  type SessionData,
  type RequestToken,
  type ClientInfo,
  type AbortVariant,
  type RequestType,
} from '../types';
import { type RequestedDataGroup } from '../hooks/useRequestedData';

// -- Page prop types per state --

export type AgentStartPageProps = {
  tags: HoruzProtocolChulakServiceSessionGuardTags;
  app?: AppInfo;
  requestType?: RequestType;
  onCancel: () => void;
  onBack?: () => void;
};

export type WaitingForPresentationPageProps = {
  onCancel: () => void;
  onBack?: () => void;
};

export type AppPairingPageProps = {
  pairingCode: string;
  onCancel: () => void;
  onCompletePairing: () => void;
  onBack?: () => void;
};

export type AppPresentationPageProps = {
  presentationCompact: string;
  client?: ClientInfo;
  requestType?: RequestType;
  requestedDataGroups?: RequestedDataGroup[];
  onAccept: () => void;
  onCancel: () => void;
};

export type DonePageProps = {
  onComplete: () => Promise<void>;
  continueInApp: boolean;
  deliveryMode: 'redirect' | 'webhook';
};

export type AbortPageProps = {
  errorCode?: number;
  errorMessage?: string;
  variant?: AbortVariant;
  onReject: () => void;
  onRestart: () => void;
  onBack?: () => void;
};

// -- Pages map --

export type ChulakPages = {
  Init: ComponentType;
  Auth: ComponentType;
  WaitingForPresentation: ComponentType<WaitingForPresentationPageProps>;
  AgentStart: ComponentType<AgentStartPageProps>;
  AppPairing: ComponentType<AppPairingPageProps>;
  AppPresentation: ComponentType<AppPresentationPageProps>;
  Done: ComponentType<DonePageProps>;
  Abort: ComponentType<AbortPageProps>;
};

// -- Component --

type Props = {
  pages: ChulakPages;
  session: SessionData;
  selectedToken: RequestToken;
  requestedDataGroups?: RequestedDataGroup[];
  onBack?: () => void;
};

/**
 * ChulakSession — Platform-agnostic Chulak flow orchestrator.
 *
 * Manages the WebSocket connection via useChulakSession and renders
 * the appropriate page component per state. Platform differences
 * (layout, footer placement) are owned by the page components
 * passed via the `pages` map.
 */
export default function ChulakSession({
  pages,
  session,
  selectedToken,
  requestedDataGroups,
  onBack,
}: Props) {
  const journey = useContext(JourneyContext)!;
  const request = session.context.request;

  const { ctx, actions, app, handleCancel, handleComplete, handleRestart } =
    useChulakSession(session, selectedToken);

  switch (ctx.state) {
    case ChulakState.Init:
      return <pages.Init />;

    case ChulakState.Auth:
      return <pages.Auth />;

    case ChulakState.AgentStart:
      return (
        <pages.AgentStart
          tags={ctx.tags}
          app={app}
          requestType={request.type}
          onCancel={handleCancel}
          onBack={onBack}
        />
      );

    case ChulakState.AppPairing:
      return (
        <pages.AppPairing
          pairingCode={ctx.pairingCode}
          onCancel={handleCancel}
          onCompletePairing={() => actions?.completePairing()}
          onBack={onBack}
        />
      );

    case ChulakState.WaitingForPresentation:
      return (
        <pages.WaitingForPresentation onCancel={handleCancel} onBack={onBack} />
      );

    case ChulakState.AppPresentation:
      return (
        <pages.AppPresentation
          presentationCompact={ctx.presentationCompact}
          client={request.client}
          requestType={request.type}
          requestedDataGroups={requestedDataGroups}
          onAccept={() => actions?.acceptPresentation()}
          onCancel={handleCancel}
        />
      );

    case ChulakState.Done:
      return (
        <pages.Done
          onComplete={handleComplete}
          continueInApp={ctx.continueInApp}
          deliveryMode={journey.getDeliveryMode()}
        />
      );

    case ChulakState.Abort: {
      const isAppUnavailable = ctx.errorMessage === 'APP_UNAVAILABLE';
      return (
        <pages.Abort
          errorCode={ctx.errorCode}
          errorMessage={ctx.errorMessage}
          variant={isAppUnavailable ? 'appUnavailable' : undefined}
          onReject={() => journey.reject()}
          onRestart={handleRestart}
          onBack={isAppUnavailable ? onBack : undefined}
        />
      );
    }
  }
}
